import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';

export type CardVariant = 'default' | 'glass' | 'solid' | 'outline';
export type CardPadding = 'none' | 'sm' | 'md' | 'lg';

interface CardProps {
  variant?: CardVariant;
  padding?: CardPadding;
  elevation?: 'none' | 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  style?: React.CSSProperties;
  children: React.ReactNode;
  onClick?: () => void;
}

export const Card: React.FC<CardProps> = ({
  variant = 'default',
  padding = 'md',
  elevation = 'md',
  className = '',
  style = {},
  children,
  onClick,
}) => {
  const { theme } = useTheme();

  // Padding classes
  const paddingClasses = {
    none: 'p-0',
    sm: 'p-4',
    md: 'p-6',
    lg: 'p-8',
  };

  // Get variant-specific styles
  const getVariantStyle = (): React.CSSProperties => {
    switch (variant) {
      case 'glass':
        return {
          background: theme.effects.glassmorphism
            ? 'rgba(255, 255, 255, 0.1)'
            : theme.colors.surface,
          backdropFilter: theme.effects.glassmorphism ? 'blur(20px)' : 'none',
          border: `1px solid ${theme.colors.outline}`,
        };
      case 'solid':
        return {
          backgroundColor: theme.colors.surface,
          border: 'none',
        };
      case 'outline':
        return {
          background: 'transparent',
          border: `2px solid ${theme.colors.outline}`,
        };
      case 'default':
      default:
        return {
          backgroundColor: theme.colors.cardBackground,
          border: 'none',
        };
    }
  };

  // Base classes
  const baseClasses = `
    rounded-[${theme.borderRadius.lg}]
    ${theme.elevation[elevation]}
    ${theme.animations.transition}
    ${onClick ? 'cursor-pointer hover:scale-[1.02]' : ''}
  `.trim().replace(/\s+/g, ' ');

  const combinedStyle = {
    ...getVariantStyle(),
    ...style,
  };

  return (
    <div
      className={`${baseClasses} ${paddingClasses[padding]} ${className}`}
      style={combinedStyle}
      onClick={onClick}
    >
      {children}
    </div>
  );
};
